import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  PurchaseOrderQuotationHeader,
  PurchaseOrderQuotation,
  PurchaseOrderQuotationLine,
  CreateQuotationHeaderDto,
  UpdateQuotationHeaderDto,
  CreateQuotationDto,
  UpdateQuotationDto,
  SelectWinnerDto,
  CancelQuotationDto,
  CancelHeaderDto,
  QuotationDocument
} from '../models/PurchaseOrderQuotation';

export interface QuotationPagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class PurchaseOrderQuotationsService {
  private readonly baseUrl = environment.microservices.shopping;
  private readonly headersPath = `${this.baseUrl}/purchase-order-quotation-headers`;
  private readonly quotationsPath = `${this.baseUrl}/purchase-order-quotations`;
  private readonly quotationDocsPath = `${this.baseUrl}/purchase-order-quotation-documents`;

  constructor(private http: HttpClient) {}

  listHeaders(
    purchaseOrderId: number,
    page = 1,
    pageSize = 20
  ): Observable<QuotationPagedResult<PurchaseOrderQuotationHeader>> {
    const params = new HttpParams()
      .set('purchaseOrderId', String(purchaseOrderId))
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.http.get<any>(this.headersPath, { params }).pipe(
      map((resp) => this.mapPaged(resp, (x) => this.mapHeader(x)))
    );
  }

  createHeader(dto: CreateQuotationHeaderDto): Observable<PurchaseOrderQuotationHeader> {
    return this.http.post<any>(this.headersPath, dto).pipe(map((x) => this.mapHeader(x)));
  }

  getHeaderById(id: number, includeProposals = true): Observable<PurchaseOrderQuotationHeader> {
    const params = new HttpParams().set('includeProposals', String(includeProposals));
    return this.http.get<any>(`${this.headersPath}/${id}`, { params }).pipe(
      map((x) => this.mapHeader(x))
    );
  }

  updateHeader(id: number, dto: UpdateQuotationHeaderDto): Observable<PurchaseOrderQuotationHeader> {
    return this.http.put<any>(`${this.headersPath}/${id}`, dto).pipe(map((x) => this.mapHeader(x)));
  }

  deleteHeader(id: number): Observable<void> {
    return this.http.delete<void>(`${this.headersPath}/${id}`);
  }

  cancelHeader(id: number, body: CancelHeaderDto): Observable<void> {
    return this.http.post<void>(`${this.headersPath}/${id}/cancel`, body);
  }

  /** PUT: corpo com lines; directorshipApproverStaffId enviado conforme o chamador (UI usa null). */
  selectWinner(headerId: number, body: SelectWinnerDto): Observable<void> {
    return this.http.put<void>(`${this.headersPath}/${headerId}/select-winner`, body);
  }

  listQuotationsForHeader(
    headerId: number,
    page = 1,
    pageSize = 20
  ): Observable<QuotationPagedResult<PurchaseOrderQuotation>> {
    const params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    return this.http.get<any>(`${this.headersPath}/${headerId}/quotations`, { params }).pipe(
      map((resp) => this.mapPaged(resp, (x) => this.mapQuotation(x)))
    );
  }

  getQuotationById(id: number): Observable<PurchaseOrderQuotation> {
    return this.http.get<any>(`${this.quotationsPath}/${id}`).pipe(map((x) => this.mapQuotation(x)));
  }

  createQuotation(dto: CreateQuotationDto): Observable<PurchaseOrderQuotation> {
    return this.http.post<any>(this.quotationsPath, dto).pipe(map((x) => this.mapQuotation(x)));
  }

  updateQuotation(id: number, dto: UpdateQuotationDto): Observable<PurchaseOrderQuotation> {
    return this.http.put<any>(`${this.quotationsPath}/${id}`, dto).pipe(map((x) => this.mapQuotation(x)));
  }

  deleteQuotation(id: number): Observable<void> {
    return this.http.delete<void>(`${this.quotationsPath}/${id}`);
  }

  cancelQuotation(id: number, body: CancelQuotationDto): Observable<void> {
    return this.http.post<void>(`${this.quotationsPath}/${id}/cancel`, body);
  }

  listDocuments(quotationId: number): Observable<QuotationDocument[]> {
    return this.http.get<any>(`${this.quotationsPath}/${quotationId}/documents`).pipe(
      map((resp) => {
        const raw = Array.isArray(resp) ? resp : resp?.items ?? resp?.Items ?? [];
        return raw.map((d: any) => this.mapDocument(d));
      })
    );
  }

  deleteDocument(documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.quotationDocsPath}/${documentId}`);
  }

  /**
   * Envia o arquivo da proposta (upload físico + registro de metadados em uma única
   * chamada) usando o endpoint dedicado do microserviço de compras:
   *
   *   POST /purchase-order-quotations/{id}/documents
   *     ?uploadedByStaffId={id}&category=...&description=...
   *     Body: multipart/form-data { file }
   *
   * O backend devolve o documento já persistido com a URL Azure Blob assinada (SAS)
   * em `filePath`.
   */
  uploadAndAttachDocument(
    quotationId: number,
    file: File,
    meta: {
      category: string;
      description?: string;
      uploadedByStaffId: number;
    }
  ): Observable<QuotationDocument> {
    if (!file) {
      return throwError(() => new Error('Selecione um arquivo para enviar.'));
    }
    if (!quotationId) {
      return throwError(() => new Error('Proposta inválida para anexo.'));
    }
    if (!meta?.uploadedByStaffId) {
      return throwError(
        () =>
          new Error(
            'Não foi possível identificar o usuário logado para registrar o anexo.'
          )
      );
    }
    if (!meta.category) {
      return throwError(() => new Error('Selecione a categoria do anexo.'));
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    let params = new HttpParams()
      .set('uploadedByStaffId', String(meta.uploadedByStaffId))
      .set('category', meta.category);
    const description = meta.description?.trim();
    if (description) {
      params = params.set('description', description);
    }

    return this.http
      .post<any>(`${this.quotationsPath}/${quotationId}/documents`, formData, { params })
      .pipe(map((x) => this.mapDocument(x)));
  }

  private mapPaged<T>(
    resp: any,
    mapItem: (raw: any) => T
  ): QuotationPagedResult<T> {
    if (Array.isArray(resp)) {
      const items = resp.map(mapItem);
      return {
        items,
        page: 1,
        pageSize: items.length || 20,
        totalItems: items.length,
        totalPages: 1
      };
    }
    const itemsRaw = resp?.items ?? resp?.Items ?? [];
    const items = Array.isArray(itemsRaw) ? itemsRaw.map(mapItem) : [];
    return {
      items,
      page: Number(resp?.page ?? resp?.Page ?? 1),
      pageSize: Number((resp?.pageSize ?? resp?.PageSize ?? items.length) || 20),
      totalItems: Number(resp?.totalItems ?? resp?.TotalItems ?? items.length),
      totalPages: Number(resp?.totalPages ?? resp?.TotalPages ?? 1)
    };
  }

  private mapHeader(raw: any): PurchaseOrderQuotationHeader {
    if (!raw) {
      return {
        id: 0,
        purchaseOrderId: 0
      };
    }
    const proposalsRaw = raw.proposals ?? raw.Proposals;
    const proposals = Array.isArray(proposalsRaw)
      ? proposalsRaw.map((p: any) => this.mapQuotation(p))
      : undefined;
    return {
      id: Number(raw.id ?? raw.Id ?? 0),
      purchaseOrderId: Number(raw.purchaseOrderId ?? raw.PurchaseOrderId ?? 0),
      headerNumber: raw.headerNumber ?? raw.HeaderNumber,
      headerDate: raw.headerDate ?? raw.HeaderDate,
      validUntil: raw.validUntil ?? raw.ValidUntil,
      createdByStaffId: raw.createdByStaffId ?? raw.CreatedByStaffId,
      status: raw.status ?? raw.Status,
      notes: raw.notes ?? raw.Notes,
      proposals
    };
  }

  private mapQuotation(raw: any): PurchaseOrderQuotation {
    if (!raw) {
      return {
        id: 0,
        purchaseOrderQuotationHeaderId: 0,
        supplierId: 0
      };
    }
    const linesRaw =
      raw.lines ??
      raw.Lines ??
      raw.items ??
      raw.Items ??
      raw.quotationItems ??
      raw.QuotationItems ??
      raw.purchaseOrderQuotationItems ??
      raw.PurchaseOrderQuotationItems;
    const lines = Array.isArray(linesRaw)
      ? linesRaw.map((x: any) => this.mapQuotationLine(x)).filter((l) => l.purchaseOrderItemId > 0)
      : undefined;
    return {
      id: Number(raw.id ?? raw.Id ?? 0),
      purchaseOrderQuotationHeaderId: Number(
        raw.purchaseOrderQuotationHeaderId ?? raw.PurchaseOrderQuotationHeaderId ?? 0
      ),
      supplierId: Number(raw.supplierId ?? raw.SupplierId ?? 0),
      supplierName: raw.supplierName ?? raw.SupplierName,
      quotationNumber: raw.quotationNumber ?? raw.QuotationNumber,
      quotationDate: raw.quotationDate ?? raw.QuotationDate,
      validUntil: raw.validUntil ?? raw.ValidUntil,
      currencyCode: raw.currencyCode ?? raw.CurrencyCode,
      itemsSubtotal: this.num(raw.itemsSubtotal ?? raw.ItemsSubtotal),
      discountAmount: this.num(raw.discountAmount ?? raw.DiscountAmount),
      freightAmount: this.num(raw.freightAmount ?? raw.FreightAmount),
      otherCostsAmount: this.num(raw.otherCostsAmount ?? raw.OtherCostsAmount),
      taxAmount: this.num(raw.taxAmount ?? raw.TaxAmount),
      totalAmount: this.num(raw.totalAmount ?? raw.TotalAmount),
      paymentTermDays: raw.paymentTermDays ?? raw.PaymentTermDays,
      deliveryLeadTimeDays: raw.deliveryLeadTimeDays ?? raw.DeliveryLeadTimeDays,
      status: raw.status ?? raw.Status,
      isWinner: Boolean(raw.isWinner ?? raw.IsWinner),
      notes: raw.notes ?? raw.Notes,
      lines: lines && lines.length ? lines : undefined
    };
  }

  private mapQuotationLine(raw: any): PurchaseOrderQuotationLine {
    const purchaseOrderItemId = Number(
      raw?.purchaseOrderItemId ??
        raw?.PurchaseOrderItemId ??
        raw?.purchaseOrderItemID ??
        raw?.PurchaseOrderItemID ??
        0
    );
    const quotedUnitPrice = this.num(
      raw?.quotedUnitPrice ??
        raw?.QuotedUnitPrice ??
        raw?.proposedUnitPrice ??
        raw?.ProposedUnitPrice ??
        raw?.negotiatedUnitPrice ??
        raw?.NegotiatedUnitPrice ??
        raw?.unitPrice ??
        raw?.UnitPrice ??
        raw?.actualUnitPrice ??
        raw?.ActualUnitPrice ??
        raw?.price ??
        raw?.Price
    );
    const quantityQuoted = this.num(raw?.quantityQuoted ?? raw?.QuantityQuoted ?? raw?.quantity ?? raw?.Quantity);
    const lineTotal = this.num(raw?.lineTotal ?? raw?.LineTotal ?? raw?.totalLineAmount ?? raw?.TotalLineAmount);
    return {
      purchaseOrderItemId,
      quotedUnitPrice: quotedUnitPrice !== undefined ? quotedUnitPrice : undefined,
      quantityQuoted: quantityQuoted !== undefined ? quantityQuoted : undefined,
      lineTotal: lineTotal !== undefined ? lineTotal : undefined
    };
  }

  private mapDocument(raw: any): QuotationDocument {
    return {
      id: Number(raw?.id ?? raw?.Id ?? 0),
      purchaseOrderQuotationId:
        raw?.purchaseOrderQuotationId ?? raw?.PurchaseOrderQuotationId,
      fileName: raw?.fileName ?? raw?.FileName,
      filePath: raw?.filePath ?? raw?.FilePath,
      contentType: raw?.contentType ?? raw?.ContentType,
      category: raw?.category ?? raw?.Category,
      description: raw?.description ?? raw?.Description,
      uploadedByStaffId: raw?.uploadedByStaffId ?? raw?.UploadedByStaffId,
      uploadedAt: raw?.uploadedAt ?? raw?.UploadedAt,
      createdAt: raw?.createdAt ?? raw?.CreatedAt
    };
  }

  private num(v: unknown): number | undefined {
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
}
